import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  type UniqueIdentifier,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {useDroppable} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {useEffect, useState} from 'react';
import type {EntryManagementSummaryType} from '../../../../../core/repositories/mypageEntryManagementsRepository';
import {ProgressCard} from '../Presenter/Progress/ProgressCard';
import {DraggableProgressCard} from './DraggableProgressCard';

// カラムIDの型定義
export type ColumnId =
  | 'activeNotCompleted'
  | 'activeCompleted'
  | 'completed'
  | 'archived'
  | 'rejected';

// カラムの設定
export const COLUMN_CONFIG: Record<
  ColumnId,
  {
    title: string;
    overallStatus: string;
    hasCompletedEntry?: boolean;
  }
> = {
  activeNotCompleted: {
    title: '未エントリー',
    overallStatus: 'active',
    hasCompletedEntry: false,
  },
  activeCompleted: {
    title: 'エントリー済み',
    overallStatus: 'active',
    hasCompletedEntry: true,
  },
  completed: {
    title: '内定',
    overallStatus: 'completed',
  },
  archived: {
    title: '辞退',
    overallStatus: 'archived',
  },
  rejected: {
    title: '落選',
    overallStatus: 'rejected',
  },
};

/**
 * ドラッグ可能なアイテムのIDからカラムIDを取得する
 * @param id ドラッグ可能なアイテムのID (形式: "columnId-entryId")
 * @returns カラムID、または見つからない場合はnull
 */
const getCardColumnId = (id: string): ColumnId | null => {
  for (const columnId of Object.keys(COLUMN_CONFIG) as ColumnId[]) {
    if (id.startsWith(columnId)) return columnId;
  }
  return null;
};

/**
 * ドラッグ可能なアイテムのIDからエントリーIDを抽出する
 * @param id ドラッグ可能なアイテムのID (形式: "columnId-entryId")
 * @returns エントリーID、または抽出できない場合はnull
 */
const extractEntryId = (id: string): number | null => {
  const parts = id.split('-');
  const entryIdStr = parts[parts.length - 1];
  if (!entryIdStr) return null;
  const entryId = Number.parseInt(entryIdStr, 10);
  return Number.isNaN(entryId) ? null : entryId;
};

type DroppableColumnProps = {
  columnId: ColumnId;
  title: string | React.ReactNode;
  entries: EntryManagementSummaryType[];
  entryManagementId?: number;
  canOpenDetail?: () => boolean;
};

const DroppableColumn: React.FC<DroppableColumnProps> = ({
  columnId,
  title,
  entries,
  entryManagementId,
  canOpenDetail,
}) => {
  const {setNodeRef} = useDroppable({
    id: columnId,
  });

  const itemIds = entries.map((entry) => `${columnId}-${entry.id}`);

  return (
    <div className="w-[430px] flex flex-col shrink-0 gap-8 bg-background-1 rounded-16">
      <div className="px-16 py-8">
        <h2 className="text-base font-bold">{title}</h2>
      </div>
      <div
        ref={setNodeRef}
        className="flex flex-col gap-16 p-16 rounded-16 overflow-auto pc:h-[60vh] min-h-[200px]"
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {entries.length > 0 ? (
            entries.map((entry) => (
              <DraggableProgressCard
                key={`${columnId}-${entry.id}`}
                id={`${columnId}-${entry.id}`}
                entry={entry}
                isSelected={entry.id === entryManagementId}
                canOpenDetail={canOpenDetail}
              />
            ))
          ) : (
            <div className="text-sm text-black-1 text-center p-16">
              該当するエントリーがありません
            </div>
          )}
        </SortableContext>
      </div>
    </div>
  );
};

type Props = {
  activeNotCompleted: EntryManagementSummaryType[];
  activeCompleted: EntryManagementSummaryType[];
  completed: EntryManagementSummaryType[];
  archived: EntryManagementSummaryType[];
  rejected: EntryManagementSummaryType[];
  entryManagementId?: number;
  onUpdateEntryStatus: (params: {
    id: number;
    overallStatus: string;
    hasCompletedEntry?: boolean;
  }) => Promise<void>;
  canOpenDetail?: () => boolean;
};

export const ProgressKanbanBoard: React.FC<Props> = ({
  activeNotCompleted,
  activeCompleted,
  completed,
  archived,
  rejected,
  entryManagementId,
  onUpdateEntryStatus,
  canOpenDetail,
}) => {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeEntry, setActiveEntry] =
    useState<EntryManagementSummaryType | null>(null);

  // 楽観的更新用のローカルstate
  const [optimisticActiveNotCompleted, setOptimisticActiveNotCompleted] =
    useState(activeNotCompleted);
  const [optimisticActiveCompleted, setOptimisticActiveCompleted] =
    useState(activeCompleted);
  const [optimisticCompleted, setOptimisticCompleted] = useState(completed);
  const [optimisticArchived, setOptimisticArchived] = useState(archived);
  const [optimisticRejected, setOptimisticRejected] = useState(rejected);

  // propsが変更されたらローカルstateを同期
  useEffect(() => {
    setOptimisticActiveNotCompleted(activeNotCompleted);
    setOptimisticActiveCompleted(activeCompleted);
    setOptimisticCompleted(completed);
    setOptimisticArchived(archived);
    setOptimisticRejected(rejected);
  }, [activeNotCompleted, activeCompleted, completed, archived, rejected]);

  // カラムIDからローカルstateのgetter/setterを取得
  type ColumnState = {
    entries: EntryManagementSummaryType[];
    setEntries: React.Dispatch<
      React.SetStateAction<EntryManagementSummaryType[]>
    >;
  };

  const getColumnStateAndSetter = (columnId: ColumnId): ColumnState => {
    switch (columnId) {
      case 'activeNotCompleted':
        return {
          entries: optimisticActiveNotCompleted,
          setEntries: setOptimisticActiveNotCompleted,
        };
      case 'activeCompleted':
        return {
          entries: optimisticActiveCompleted,
          setEntries: setOptimisticActiveCompleted,
        };
      case 'completed':
        return {
          entries: optimisticCompleted,
          setEntries: setOptimisticCompleted,
        };
      case 'archived':
        return {entries: optimisticArchived, setEntries: setOptimisticArchived};
      case 'rejected':
        return {entries: optimisticRejected, setEntries: setOptimisticRejected};
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const {active} = event;
    setActiveId(active.id);

    // アクティブなエントリーを見つける
    const allEntries = [
      ...optimisticActiveNotCompleted,
      ...optimisticActiveCompleted,
      ...optimisticCompleted,
      ...optimisticArchived,
      ...optimisticRejected,
    ];
    const draggedEntry = allEntries.find((entry) =>
      String(active.id).endsWith(`-${entry.id}`),
    );
    setActiveEntry(draggedEntry || null);
  };

  // ドラッグ状態をクリーンアップする関数
  const clearDragState = () => {
    setActiveId(null);
    setActiveEntry(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const {active, over} = event;

    if (!over) {
      clearDragState();
      return;
    }

    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);

    const sourceColumnId = getCardColumnId(activeIdStr);

    // ドロップ先がカラム自体の場合はそのカラムID、カードの場合はそのカードのカラムIDを取得
    const targetColumnId = Object.keys(COLUMN_CONFIG).includes(overIdStr)
      ? (overIdStr as ColumnId)
      : getCardColumnId(overIdStr);

    if (!sourceColumnId || !targetColumnId) {
      clearDragState();
      return;
    }

    // 同じカラム内の移動は無視
    if (sourceColumnId === targetColumnId) {
      clearDragState();
      return;
    }

    // エントリーIDを抽出
    const entryId = extractEntryId(activeIdStr);
    if (!entryId) {
      clearDragState();
      return;
    }

    // 楽観的更新: 元のカラムから削除し、ターゲットカラムに追加
    const sourceState = getColumnStateAndSetter(sourceColumnId);
    const targetState = getColumnStateAndSetter(targetColumnId);
    const movedEntry = sourceState.entries.find((e) => e.id === entryId);

    if (movedEntry) {
      sourceState.setEntries(
        sourceState.entries.filter((e) => e.id !== entryId),
      );
      targetState.setEntries([...targetState.entries, movedEntry]);
    }

    // 楽観的更新後にオーバーレイをクリア
    clearDragState();

    // ターゲットカラムの設定を取得
    const targetConfig = COLUMN_CONFIG[targetColumnId];

    // ステータスを更新
    try {
      await onUpdateEntryStatus({
        id: entryId,
        overallStatus: targetConfig.overallStatus,
        hasCompletedEntry: targetConfig.hasCompletedEntry,
      });
    } catch (_error) {
      // エラーはonUpdateEntryStatus内で処理されることを想定
      // 追加のエラーハンドリングが必要な場合はここに実装
    }
  };

  // カラムの定義をループで生成（COLUMN_CONFIGから動的に生成）
  const columns = Object.entries(COLUMN_CONFIG).map(([columnId, config]) => ({
    columnId: columnId as ColumnId,
    title: config.title,
    entries: getColumnStateAndSetter(columnId as ColumnId).entries,
  }));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-16">
        {columns.map(({columnId, title, entries}) => (
          <DroppableColumn
            key={columnId}
            columnId={columnId}
            title={title}
            entries={entries}
            entryManagementId={entryManagementId}
            canOpenDetail={canOpenDetail}
          />
        ))}
      </div>
      <DragOverlay>
        {activeId && activeEntry ? (
          <div className="opacity-80">
            <ProgressCard entry={activeEntry} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
